import React, { useState } from "react";
import { Check } from "lucide-react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { motion, AnimatePresence } from "framer-motion";
import "../styling/ItProjectsLayoutDesktop.css";

const projects = [
  {
    title: "Networking",
    description: [
      "Performed GPON network configuration , diagnostics & automated configuration using Python scripts",
      "Installed and configured MiniFTTO systems to ensure efficient data transmission.",
      "Diagnosed and resolved camera-related issues, including network connectivity and image quality problems.",
      "Assigned static IP addresses to CCTV cameras for improved system organization and control.",
      "Configured and maintained IVS and NVR devices to support reliable CCTV operations.",
    ],
    location: "Jadriya Mall\n(Baghdad)",
    image: "",
    year: "Oct 2024 - Jan 2025",
    progress: 100,
  },
  {
    title: "Parking Management",
    description: [
      "Led the installation of a parking management system with controllers and over 700 sensors to automate vehicle entry and exit.",
      "Configured the integration of sensors with software for real-time parking management.",
      "Set up guiding screens to help users navigate available parking spaces, including a large outdoor display.",
      "Set up ticket machine software and diagnosed both hardware and software issues",
    ],
    location: "Jadriya Mall\n(Baghdad)",
    image: "",
    year: "Nov 2024 – Jan 2025",
    progress: 100,
  },
  {
    title: "Nurse Call System",
    description: [
      "Led the installation of a nurse call system with isolators and over 100 devices (call points, pull cords, over-door lights) to ensure efficient patient-to-staff communication.",
      "Configured integration of nurse call devices with central monitoring software for real-time call management.",
      "Set up display panels and over-door indicator lights to help staff quickly locate patients requiring assistance.",
      "Conducted thorough testing to ensure all components operated smoothly and reliably.",
    ],

    location: "Ibn Sina Hospital\n(Green zone)",
    image: "",
    year: "April 2025 – May 2025",
    progress: 100,
  },
  {
    title: "Networking",
    description: [
      "Deployed and set up a fiber-optic network with managed network switches (VLANs).",
      "Configured a Safeeye PBX telephony system with over 200 IP phones",
      "Automated IP phones configuration using Python scripts with Selenium and ChromeDriver.",
    ],

    location: "Ibn Sina Hospital\n(Green zone)",
    image: "",
    year: "April 2025 – Present",
    progress: 80,
  },
];

const ItemTypes = {
  PROJECT: "project",
};

const ProjectCard = ({ project, index }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.PROJECT,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      style={{
        backgroundColor: "#1f2937",
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "20px",
        color: "white",
        opacity: isDragging ? 0.6 : 1,
        cursor: "grab",
        boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
        transition: "transform 0.2s, box-shadow 0.3s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.03)";
        e.currentTarget.style.boxShadow = "0 8px 20px rgba(59, 130, 246, 0.6)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.4)";
      }}
    >
      <h3>{project.title}</h3>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "8px",
        }}
      >
        <span
          style={{
            backgroundColor: "#10b981",
            borderRadius: "6px",
            padding: "2px 8px",
            fontSize: "12px",
            fontWeight: "bold",
          }}
        >
          {project.location.split("\n").map((line, i) => (
            <React.Fragment key={i}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </span>

        <span style={{ fontSize: "12px", color: "#9ca3af" }}>
          {project.year}
        </span>
      </div>
      {/* 
       <h3 style={{ fontSize: '18px', margin: '0 0 6px' }}>{project.title}</h3>
   <p style={{ fontSize: '14px', color: '#d1d5db' }}>{project.description}</p>
      */}
    </div>
  );
};

const DropZone = ({ selectedProject, onDropProject }) => {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.PROJECT,
    drop: (item) => onDropProject(item.index),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }));

  const isActive = isOver && canDrop;

  return (
    <div
      ref={drop}
      style={{
        flex: 1,
        minHeight: "360px",
        padding: "32px",
        backgroundColor: "#111827",
        border: isActive ? "3px dashed #3b82f6" : "3px dashed #374151",
        borderRadius: "16px",
        color: "#d1d5db",
        display: "flex",
        flexDirection: "column",
        justifyContent: selectedProject ? "flex-start" : "center",
        alignItems: "center",
        transition: "0.3s",
        boxShadow: isActive ? "0 0 20px rgba(59,130,246,0.5)" : "none",
      }}
    >
      <AnimatePresence mode="wait">
        {selectedProject ? (
          <motion.div
            key={selectedProject.title}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.4 }}
            style={{
              backgroundColor: "#1f2937",
              padding: "32px",
              borderRadius: "16px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
              maxWidth: "800px",
              margin: "auto",
              color: "#f3f4f6",
              textAlign: "left",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "16px",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <div>
                <h2 style={{ fontSize: "28px", fontWeight: "600" }}>
                  {selectedProject.title}
                </h2>
                <p style={{ color: "#9ca3af", fontSize: "16px" }}>
                  {selectedProject.tech} — {selectedProject.year}
                </p>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  height: "10px",
                  width: "100%",
                  backgroundColor: "#374151",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${selectedProject.progress}%`,
                    backgroundColor: "#3b82f6",
                    transition: "width 0.5s",
                  }}
                />
              </div>
              <p
                style={{
                  textAlign: "right",
                  fontSize: "12px",
                  color: "#9ca3af",
                  marginTop: "5px",
                }}
              >
                {selectedProject.progress}% Complete
              </p>
            </div>

            <ul
              style={{
                paddingLeft: "1rem",
                lineHeight: "1.8",
                fontSize: "16px",
              }}
            >
              {selectedProject.description.map((line, index) => (
                <li
                  key={index}
                  style={{
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <span style={{ color: "red", marginRight: "10px" }}>✔</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ) : (
          <p style={{ fontSize: "16px", color: "#6b7280" }}>
            {isActive ? "Drop it here!" : "Drag a project card to view details"}
          </p>
        )}
      </AnimatePresence>
    </div>
  );
};

const ItProjectsLayoutDektop = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            padding: "40px",
            backgroundColor: "#0f172a",
            minHeight: "100vh",
            gap: "40px",
            fontFamily: "Segoe UI, sans-serif",
          }}
        >
          <div className="project-scrollbar">
            <h2 style={{ color: "#60a5fa", marginBottom: "70px" }}> </h2>
            {projects.map((project, index) => (
              <ProjectCard key={index} project={project} index={index} />
            ))}
          </div>

          <DropZone
            selectedProject={selectedProject}
            onDropProject={(i) => setSelectedProject(projects[i])}
          />
        </div>
      </DndProvider>
    </>
  );
};

export default ItProjectsLayoutDektop;
